class AnchoredPopoverComponent extends HTMLElement {
  constructor() {
    super();
    this.interactionDelay = 200;
    this.popoverTimeout = null;
    this.onTriggerEnter = this.onTriggerEnter.bind(this);
    this.onTriggerLeave = this.onTriggerLeave.bind(this);
    this.onPopoverEnter = this.onPopoverEnter.bind(this);
    this.onPopoverLeave = this.onPopoverLeave.bind(this);
    this.onResize = this.onResize.bind(this);
    this.updatePosition = this.updatePosition.bind(this);
  }

  connectedCallback() {
    this.trigger = this.querySelector("[data-popover-trigger]");
    this.popoverPanel = this.querySelector("[data-popover-panel]");

    if (!this.trigger || !this.popoverPanel) return;

    if (this.dataset.closeOnResize !== undefined) {
      this.popoverPanel.addEventListener("beforetoggle", (event) => {
        window[
          event.newState === "open" ? "addEventListener" : "removeEventListener"
        ]("resize", this.onResize);
      });
    }

    if (this.dataset.hoverTriggered !== undefined) {
      this.trigger.addEventListener("pointerenter", this.onTriggerEnter);
      this.trigger.addEventListener("pointerleave", this.onTriggerLeave);
      this.popoverPanel.addEventListener("pointerenter", this.onPopoverEnter);
      this.popoverPanel.addEventListener("pointerleave", this.onPopoverLeave);
    }

    if (!CSS.supports("position-anchor: --trigger")) {
      this.popoverPanel.addEventListener("beforetoggle", this.updatePosition);
      window.requestAnimationFrame(this.updatePosition);
    }
  }

  disconnectedCallback() {
    if (this.trigger && this.popoverPanel) {
      this.trigger.removeEventListener("pointerenter", this.onTriggerEnter);
      this.trigger.removeEventListener("pointerleave", this.onTriggerLeave);
      this.popoverPanel.removeEventListener("pointerenter", this.onPopoverEnter);
      this.popoverPanel.removeEventListener("pointerleave", this.onPopoverLeave);
      this.popoverPanel.removeEventListener("beforetoggle", this.updatePosition);
    }

    window.removeEventListener("resize", this.onResize);

    if (this.popoverTimeout) {
      clearTimeout(this.popoverTimeout);
      this.popoverTimeout = null;
    }
  }

  onTriggerEnter() {
    this.trigger.dataset.hoverActive = "true";

    if (!this.isPopoverOpen()) {
      this.popoverTimeout = window.setTimeout(() => {
        if (this.trigger?.dataset.hoverActive !== "true") return;
        this.popoverPanel?.showPopover?.();
      }, this.interactionDelay);
    }
  }

  onTriggerLeave() {
    delete this.trigger.dataset.hoverActive;

    if (this.popoverTimeout) {
      clearTimeout(this.popoverTimeout);
      this.popoverTimeout = null;
    }

    if (this.isPopoverOpen()) {
      this.popoverTimeout = window.setTimeout(() => {
        this.popoverPanel?.hidePopover?.();
      }, this.interactionDelay);
    }
  }

  onPopoverEnter() {
    if (this.popoverTimeout) {
      clearTimeout(this.popoverTimeout);
      this.popoverTimeout = null;
    }
  }

  onPopoverLeave() {
    this.popoverTimeout = window.setTimeout(() => {
      this.popoverPanel?.hidePopover?.();
    }, this.interactionDelay);
  }

  onResize() {
    if (this.isPopoverOpen()) {
      this.popoverPanel.hidePopover();
    }
  }

  isPopoverOpen() {
    return Boolean(this.popoverPanel?.matches(":popover-open"));
  }

  getVerticalPositionAnchor() {
    const anchorName = this.dataset.verticalPositionAnchor?.trim();
    if (!anchorName) return null;

    const selector = `[data-anchor-name="${CSS.escape(anchorName)}"]`;
    const scope = this.closest("[data-anchor-scope], tr, li, section, article, form, td, div");
    const scopedAnchor = scope?.querySelector(selector);
    if (scopedAnchor instanceof HTMLElement) return scopedAnchor;

    const root = this.getRootNode();
    const fallbackAnchor = root?.querySelector?.(selector);

    return fallbackAnchor instanceof HTMLElement ? fallbackAnchor : null;
  }

  updatePosition() {
    if (!this.popoverPanel || !this.trigger) return;

    const triggerRect = this.trigger.getBoundingClientRect();
    const verticalAnchor = this.getVerticalPositionAnchor() || this.trigger;
    const verticalRect = verticalAnchor.getBoundingClientRect();

    this.popoverPanel.style.setProperty("--anchor-top", `${verticalRect.top}`);
    this.popoverPanel.style.setProperty(
      "--anchor-right",
      `${window.innerWidth - triggerRect.right}`
    );
    this.popoverPanel.style.setProperty(
      "--anchor-bottom",
      `${window.innerHeight - verticalRect.bottom}`
    );
    this.popoverPanel.style.setProperty("--anchor-left", `${triggerRect.left}`);
    this.popoverPanel.style.setProperty("--anchor-height", `${verticalRect.height}`);
    this.popoverPanel.style.setProperty("--anchor-width", `${triggerRect.width}`);
  }
}

if (!customElements.get("anchored-popover-component")) {
  customElements.define("anchored-popover-component", AnchoredPopoverComponent);
}
